import {createContext, useContext, useState, ReactNode, useEffect} from 'react';
import { DateObject, formatDate } from "@/lib/interfaces/settings";
import {useCookies} from "react-cookie";
import {AuthContextProps, AuthToken, User} from "@/lib/interfaces/user";
import {getAuthenticatedUser, getRefreshToken, logout as userLogout} from "@/lib/user";
import moment from "moment";

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export function useAuth(): AuthContextProps {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User|null>(null);
    const [accessToken, setAccessToken] = useState<string>('');
    const [cookies, setCookie, removeCookie] = useCookies(["user"]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (cookies.user) {
            const expiryTime = moment(cookies.user.expiry_date).subtract(10, "minutes");
            if (moment().isAfter(expiryTime)) {
                console.log("expiring");
                // setLoading(true);
                // refresh(cookies.user.access_token)
                //     .then((res) => {
                //         setCookie("user", res, {
                //             path: "/",
                //             maxAge: res.expires_in - (60*5),
                //         });
                //         setLoading(false);
                //     })
                //     .catch((e) => {
                //         console.log(e);
                //         // removeCookie("user");
                //     })
                //     .finally(() => {
                //         setLoading(false);
                //     });
            }
            setLoading(true);
            setAccessToken(cookies.user.access_token);
            getAuthenticatedUser(cookies.user.access_token)
                .then((res) => {
                    setUser(res);
                    setLoading(false);
                })
                .finally(() => {
                    setLoading(false);
                })
        } else {
            setUser(null);
            setLoading(false);
        }
    }, [cookies.user]);

    const refresh = async(token: string) => {
        return getRefreshToken(token);
    }

    const login = async (email: string, password: string, remember_me: boolean = false) => {
        const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ "email": email, "password": password }),
        });

        const data = await res.json();

        if (!res.ok) {
            throw new Error(data.error);
        }

        const authToken: AuthToken = data;
        const expiryDate = new Date;
        expiryDate.setSeconds(expiryDate.getSeconds() + Number(authToken.expires_in));
        authToken.expiry_date = expiryDate;
        authToken.remember = remember_me;
        setCookie("user", authToken, {
            path: "/",
            maxAge: remember_me ? authToken.expires_in : undefined,
        });
    };

    const register = async (email: string, password: string, date: DateObject, policy: boolean, marketing_mail: boolean) => {
        const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email,
                password,
                password_confirmation: password,
                policy,
                birthdate: formatDate(date),
                marketing_mail,
            }),
        });

        if (!res.ok) {
            const data = await res.json();
            throw data;
        }
    };

    const logout = () => {
        if (cookies.user) {
            const accessToken = cookies.user.access_token;
            removeCookie("user");
            setLoading(true);
            userLogout(accessToken)
                .finally(() => {
                    setLoading(false);
                })
        }
    }

    const value = {
        user,
        accessToken,
        login,
        register,
        logout,
        refresh,
        loading
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}